<template>
  <div class="create-customer">
  <div v-if="pests.length > 0">
    <h1>Which pests do you need help with?</h1>
    <i>click on the picture to choose</i>
    <div class="pest-selection">
      <v-row>
        <v-col
          cols="6"
          sm="6"
          md="4"
          lg="3"
          v-for="(pest, index) in pests"
          @click="togglePest(pest)"
          :key="index"
          class="d-flex flex-column align-center justify-center text-center pest-wrap"
        >
          <img
            :src="pest.image"
            :class="{ 'pest-selected': pest.selected }"
            alt=""
            class="pest-img"
          />
          <div class="mt-2 pest-name">{{ pest.name }}</div>
          <v-card-text class="mt-2 pest-description d-none d-sm-block">{{ pest.description }}</v-card-text>
        </v-col>
      </v-row>
    </div>
  </div>
  <h1>What is the best way to contact you?</h1>
    <v-card-text>
      <v-form v-model="valid" @submit.prevent>
        <v-text-field v-model="customerDetails.fname" :rules="nameRules" label="First Name" placeholder="John" variant="outlined"></v-text-field>
        <v-text-field v-model="customerDetails.lname" :rules="nameRules" label="Last Name" placeholder="Doe" variant="outlined"></v-text-field>
        <v-text-field v-model="customerDetails.phone1" :rules="phoneRules" label="Phone Number" placeholder="1234567890" variant="outlined"></v-text-field>
        <v-text-field v-model="customerDetails.email" :rules="emailRules" label="Email Address" placeholder="johndoe@gmail.com" variant="outlined" type="email"></v-text-field>
        <v-text-field v-model="customerDetails.address" label="Address" placeholder="123 Main St" variant="outlined"></v-text-field>
        <v-text-field v-model="customerDetails.city" label="City" placeholder="San Diego" variant="outlined"></v-text-field>
        <v-text-field v-model="customerDetails.state" :rules="stateRules" label="State" placeholder="CA" variant="outlined"></v-text-field>
        <v-text-field v-model="customerDetails.zip" :rules="zipRules" label="Zip" placeholder="92127" variant="outlined"></v-text-field>
      <!-- Consent Checkbox -->
      <v-checkbox class="consent"
        v-model="consentGiven"
        :rules="[v => !!v || 'Consent is required']"
        :label="`Yes, ${companyName} may contact me via call or text message. Carrier data rates may apply.`"
      ></v-checkbox>
      <p class="consent-instructions">
        By submitting this request, I validate that I am 18 years of age or older. Additionally, I give {{ companyName }} permission to email me, or to call or text me at the number provided regarding services from {{ companyName }} using automated technology. I understand my consent is not a condition of purchase.
      </p>

      <!-- Submit Button (disabled until form is valid and consent is given) -->
      <v-btn :disabled="!valid || !consentGiven" type="submit" color="primary" size="large" class="font-weight-bold" @click="createCustomer">Next</v-btn>
    </v-form>
    </v-card-text>
    <p v-if="error">{{ error }}</p>
    <p v-if="result">{{ result }}</p>
    <!--<p>Data received from Parent: {{ receivedPlace }}</p>-->
  </div>
</template>

<script>
import axios from 'axios';

const sanDiegoZipCodes = [
  '91901','91902','91905','91906','91910','91911','91913','91914','91915','91916',
  '91917','91931','91932','91934','91935','91941','91942','91945','91948','91950',
  '91962','91963','91977','91978','91980','92003','92004','92007','92008','92009',
  '92010','92011','92014','92019','92020','92021','92024','92025','92026','92027',
  '92028','92029','92036','92037','92040','92054','92055','92056','92057','92058',
  '92059','92060','92061','92064','92065','92066','92067','92068','92069','92070',
  '92071','92075','92078','92081','92082','92083','92084','92086','92091','92093',
  '92101','92102','92103','92104','92105','92106','92107','92108','92109','92110',
  '92111','92113','92114','92115','92116','92117','92118','92119','92120','92121',
  '92122','92123','92124','92126','92127','92128','92129','92130','92131','92132',
  '92134','92135','92136','92139','92140','92145','92147','92152','92154','92155',
  '92173','92182','92191','92192','92193','92195','92196','92197','92198','92199'
];

export default {
  props: {
    receivedPlace: Object,
    companyConfig: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      customerDetails: {
        customerID: '',
        sourceID: this.companyConfig?.integration?.sourceID || 10207,
        fname: '',
        lname: '',
        phone1: '',
        email: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        smsReminders: 1,
        phoneReminders: 1,
        emailReminders: 1,
        notes: '',
      },
      valid: false,
      consentGiven: false,
      nameRules: [
        value => value ? true : 'Name is required.',
        value => (value?.length <= 20) ? true : 'Name must be less than 20 characters.',
      ],
      emailRules: [
        value => value ? true : 'E-mail is required.',
        value => (/.+@.+\..+/.test(value)) ? true : 'E-mail must be valid.',
      ],
      phoneRules: [
        value => !!value || 'Phone number is required.',
        value => /^\d{10}$/.test(value) || 'Phone number must be 10 digits.',
      ],
      stateRules: [
        v => !!v || 'State is required.',
        v => /^[A-Z]{2}$/.test(v) || 'State must be 2 uppercase letters.',
      ],
      result: null,
      error: null,
      pests: [], // Reactive pest list populated in mounted()
      apiBaseUrl: process.env.NODE_ENV === 'development'
        ? 'http://localhost:3000/api'  // Local server
        : 'https://api.getpestcontrol.services/api'  // Production server
    };
  },
  computed: {
    companyName() {
      return this.companyConfig?.companyName || 'Mission Pest Control';
    },
    zipRules() {
      const baseRules = [
        v => !!v || 'ZIP code is required.',
        v => /^\d{5}$/.test(v) || 'ZIP code must be 5 digits.',
      ];

      const zipValidation = this.companyConfig?.integration?.zipValidation;

      // If zipValidation config exists
      if (zipValidation) {
        // If enabled: true, apply custom validation
        if (zipValidation.enabled && zipValidation.pattern) {
          // Convert string pattern to RegExp if needed
          const pattern = typeof zipValidation.pattern === 'string'
            ? new RegExp(zipValidation.pattern)
            : zipValidation.pattern;

          baseRules.push(
            v => pattern.test(v) || zipValidation.message || 'Invalid ZIP code for service area.'
          );
        }
        // If enabled: false, don't add any custom validation (accept any 5-digit ZIP)
      } else {
        // No zipValidation config at all - use default San Diego validation
        baseRules.push(
          v => sanDiegoZipCodes.includes(v) || 'ZIP code must be in San Diego County.'
        );
      }

      return baseRules;
    }
  },
  mounted() {
    // Initialize pests list based on company config
    this.initializePests();
    // Initial population of fields if receivedPlace is already provided
    this.updateCustomerDetails(this.receivedPlace);
  },
  watch: {
    receivedPlace: {
      handler(newValue) {
        this.updateCustomerDetails(newValue);
      },
      immediate: true, // Immediately run the handler upon mounting
      deep: true,      // enable deep watching if receivedPlace is a complex object
    },
    'customerDetails.state'(newVal) {
      this.customerDetails.state = newVal.toUpperCase();
    },
  },
  methods: {
    initializePests() {
      // Master list of all available pests with local images
      const allPests = [
        {
          id: "ants",
          name: "Ants",
          description: "Seeing one ant likely means many more are nearby.",
          image: require("@/assets/bugs/ants.png"),
          selected: false,
        },
        {
          id: "fleas",
          name: "Fleas",
          description: "Often found in pet bedding and resting areas.",
          image: require("@/assets/bugs/fleas.png"),
          selected: false,
        },
        {
          id: "roaches",
          name: "Roaches",
          description: "Hide well and multiply quickly in food and water sources.",
          image: require("@/assets/bugs/roaches.png"),
          selected: false,
        },
        {
          id: "wasps",
          name: "Wasps",
          description: "Aggressive when threatened and common around the home.",
          image: require("@/assets/bugs/wasps.png"),
          selected: false,
        },
        {
          id: "spiders",
          name: "Spiders",
          description: "Create webs and hide in corners or quiet areas.",
          image: require("@/assets/bugs/spiders.png"),
          selected: false,
        },
        {
          id: "rodents",
          name: "Rodents",
          description: "Cause damage and spread disease while seeking food or shelter.",
          image: require("@/assets/bugs/rodents.png"),
          selected: false,
        },
      ];

      // Filter pests based on company config
      // Config format: { pests: { ants: true, fleas: false, ... } }
      const pestConfig = this.companyConfig?.formFields?.pests;

      if (pestConfig && typeof pestConfig === 'object' && !Array.isArray(pestConfig)) {
        // Filter based on boolean flags from API
        this.pests = allPests.filter(pest => pestConfig[pest.id] === true);
      } else {
        // If no config, show all pests
        this.pests = allPests;
      }
    },
    updateCustomerDetails(newValue) {
      if (newValue) {
        let streetNumberComponent = newValue.address_components.find(component => component.types.includes('street_number'))?.long_name || '';
        let routeComponent = newValue.address_components.find(component => component.types.includes('route'))?.long_name || '';
        let localityComponent = newValue.address_components.find(component => component.types.includes('locality'))?.long_name || '';
        let administrativeAreaComponent = newValue.address_components.find(component => component.types.includes('administrative_area_level_1'))?.short_name || '';
        let postalCodeComponent = newValue.address_components.find(component => component.types.includes('postal_code'))?.long_name || '';

        let lat = newValue.geometry?.location?.lat || '';
        let lng = newValue.geometry?.location?.lng || '';

        this.customerDetails.address = `${streetNumberComponent} ${routeComponent}`.trim();
        this.customerDetails.city = localityComponent;
        this.customerDetails.state = administrativeAreaComponent;
        this.customerDetails.zip = postalCodeComponent;
        this.customerDetails.lat = lat;
        this.customerDetails.lng = lng;
      }
    },
    togglePest(pest) {
      pest.selected = !pest.selected;

      const selected = this.pests
        .filter(p => p.selected)
        .map(p => p.name);

      this.customerDetails.notes = selected.length
        ? `Pests to focus on: ${selected.join(', ')}`
        : '';
    },
    async createCustomer() {
      if (!this.valid) return;
      try {
        const response = await axios.post(`${this.apiBaseUrl}/customer/create`, this.customerDetails);
        this.customerDetails.customerID = response.data.result;
        // localStorage is now handled by RegistrationForm's watcher
        this.$emit('send-customer', this.customerDetails);
      } catch (error) {
        this.error = error.message;
        console.error("Error creating customer:", error);
      }
    }
  }
}
</script>

<style>
.pest-selection {
  padding: 20px;
}

.pest-wrap {
  cursor: pointer;
  border: 2px solid var(--color-surface);
  transition: border 0s ease, box-shadow 0.3s ease;
}

.pest-wrap:hover {
  border: 2px solid var(--brand-primary);
  border-radius: var(--button-border-radius);
  box-shadow: 0 0 10px rgba(var(--brand-primary-rgb), 0.2);
}

.pest-img {
  width: 75px;
  height: 75px;
  object-fit: contain;
  transition: all 0.3s ease;
  filter: grayscale(100%); /* Default to grayscale */
}

.pest-selected {
  /* Keep grayscale but apply theme color glow */
  filter: grayscale(100%)
          drop-shadow(0 0 8px var(--brand-primary))
          drop-shadow(0 0 4px var(--brand-primary))
          brightness(1.1);
}

.pest-name {
  text-transform: uppercase;
  font-weight: bold;
}

.pest-description {
  text-align: center;
  font-size: 0.9rem;
  max-width: 300px;
}
.consent-instructions {
  font-size: 0.7rem;
  color: var(--color-text-secondary);
  margin-top: -5px;
  margin-bottom: 20px;
}
.v-checkbox-btn .v-label {
  font-size: 0.775rem;
}
</style>
